import { useEffect, useState, useRef } from "react"

import { AIModel } from "@llumiverse/core"
import { Check, Cpu } from "lucide-react"
import { toast } from "sonner"

import { useWatchSearchResult } from "@plugins/providers/model/AvailableModelContext"

import { Button } from "@ui/button"
import { Input } from "@ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@ui/popover"

interface AgentModelSelectionProps {
    model: string | undefined
    showSelection?: boolean
    disabled?: boolean
    onChange?: (model: AIModel | undefined) => void
}
export function AgentModelSelection({ model, showSelection = false, disabled = false, onChange }: AgentModelSelectionProps) {
    const { search, models, error } = useWatchSearchResult()

    const [open, setOpen] = useState(false)
    const [currentModel, setCurrentModel] = useState<AIModel | undefined>(undefined)
    const [filterValue, setFilterValue] = useState("")

    const lastOnChangeModel = useRef<AIModel | undefined>(undefined)

    const filteredOptions = models?.filter((model) => model.name.toLowerCase().includes(filterValue.toLowerCase()) && model.id !== currentModel?.id)

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilterValue(e.target.value)
    }

    const handleCurrentModelChange = (model: AIModel) => {
        setCurrentModel(model)
        setOpen(false)
        setFilterValue("")
    }

    useEffect(() => {
        if (!models.length) {
            return
        }

        setCurrentModel(models.find((m) => m.id === model || m.name === model) || undefined)
    }, [model, models])

    useEffect(() => {
        if (currentModel !== lastOnChangeModel.current) {
            lastOnChangeModel.current = currentModel
            onChange?.(currentModel)
        }
    }, [currentModel, onChange])

    useEffect(() => {
        if (!error) {
            return
        }

        toast.error("Error fetching available models", {
            description: error.message,
            duration: 5000,
        })
    }, [error])

    useEffect(() => {
        if (models.length) {
            return
        }

        search.search()
    }, [models.length, search])

    return (
        <>
            {
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild disabled={disabled || models.length === 0}>
                        {showSelection && model ? (
                            <div className="flex items-center gap-2">
                                <Button variant="icon" size="icon" className="flex-shrink-0">
                                    <Cpu />
                                </Button>
                                <span className="text-sm">{model}</span>
                            </div>
                        ) : (
                            <Button variant="icon" size="icon">
                                <Cpu />
                            </Button>
                        )}
                    </PopoverTrigger>
                    <PopoverContent side="bottom" align="start" className="p-1">
                        {models.length > 5 && (
                            <div className="p-2">
                                <Input placeholder="Search Models..." value={filterValue} onChange={handleInputChange} />
                            </div>
                        )}
                        {models.length > 0 && (
                            <div className="flex flex-col gap-1 max-h-[200px] overflow-y-auto">
                                {currentModel && (
                                    <Button variant="ghost" className="text-xs text-left justify-between w-full bg-gray-100">
                                        {currentModel.name}
                                        <Check />
                                    </Button>
                                )}
                                {filteredOptions.length > 0 ? (
                                    filteredOptions.map((model) => (
                                        <Button key={`${model.id} - ${model.environment}`} variant="ghost" className="text-xs text-left justify-between w-full" onClick={() => handleCurrentModelChange(model)}>
                                            {model.name}
                                        </Button>
                                    ))
                                ) : (
                                    <div className="text-xs text-gray-500 p-2">No models found</div>
                                )}
                            </div>
                        )}
                    </PopoverContent>
                </Popover>
            }
        </>
    )
}
